(() => {
  if (customElements.get('cgs-downloads')) return;

  const keyName = (key) => key.toLowerCase().replace(/[^a-z0-9]/g, '');
  const scalar = (value) => (typeof value === 'string' || typeof value === 'number')
    ? String(value).trim().slice(0, 200) : '';
  const field = (object, names) => {
    if (!object || typeof object !== 'object' || Array.isArray(object)) return '';
    for (const name of names) {
      const entry = Object.entries(object).find(([key, value]) => keyName(key) === name && scalar(value));
      if (entry) return scalar(entry[1]);
    }
    return '';
  };
  const metadata = (parents, names) => {
    for (const parent of parents) {
      const value = field(parent, names);
      if (value) return value;
    }
    return '';
  };
  const secureUrl = (value, base) => {
    if (typeof value !== 'string' || !value.trim()) return '';
    // A placeholder such as "coming soon" is not a relative download path.
    if (!/^(?:https:\/\/|\/|\.\.?\/)/i.test(value.trim())
      && !/^[^\s?#]+\.(?:exe|msi|zip|dmg|pkg|deb|rpm|appimage)(?:[?#].*)?$/i.test(value.trim())) return '';
    try {
      const url = new URL(value.trim(), base);
      return url.protocol === 'https:' && !url.username && !url.password ? url.href : '';
    } catch (error) {
      return '';
    }
  };
  const compareVersion = (left, right) => {
    const parts = (value) => /^v?\d+(?:\.\d+)*$/i.test(value) ? value.replace(/^v/i, '').split('.').map(Number) : [];
    const a = parts(left);
    const b = parts(right);
    if (!a.length || !b.length) return 0;
    for (let index = 0; index < Math.max(a.length, b.length); index += 1) {
      const difference = (b[index] || 0) - (a[index] || 0);
      if (difference) return difference;
    }
    return 0;
  };

  // Unknown manifests may nest the universal Setup under latest, downloads,
  // windows, assets, or releases. Keep metadata with its own URL, never with a
  // neighbouring architecture-specific package, and never expose raw HTML.
  const normalizeManifest = (manifest, base, platform) => {
    const candidates = [];
    let visited = 0;
    const walk = (node, path = [], parents = [], depth = 0) => {
      visited += 1;
      if (!node || typeof node !== 'object' || depth > 8 || visited > 500) return;
      const ancestry = Array.isArray(node) ? parents : [node, ...parents];
      Object.entries(node).forEach(([key, value]) => {
        const keyId = keyName(key);
        const nextPath = [...path, keyId];
        if (value && typeof value === 'object') {
          walk(value, nextPath, ancestry, depth + 1);
          return;
        }
        if (typeof value !== 'string') return;
        const urlField = /^(?:url|href|download|downloadurl|downloadlink|setup|setupurl|installer|installerurl|asseturl|universal|universalurl|universalsetupurl|stub|stuburl|stubsetupurl)$/.test(keyId);
        if (!urlField) return;
        const url = secureUrl(value, base);
        if (!url) return;
        const pathname = new URL(url).pathname;
        if (/\.(?:json|txt|md|sha256|sha512|sig|png|jpe?g|svg)$/i.test(pathname)) return;
        const descriptors = ancestry.flatMap((parent) => ['type', 'kind', 'architecture', 'arch', 'platform', 'os', 'name', 'filename', 'file']
          .map((name) => field(parent, [name])));
        const descriptor = [...nextPath, ...descriptors, pathname].join(' ').toLowerCase();
        const universal = /universal|stub|bootstrap/.test(descriptor);
        if (/firmware|changelog|release.?notes/.test(descriptor)) return;
        if (platform === 'windows' && /(?:macos|darwin|linux)|\.(?:dmg|pkg|deb|rpm|appimage)$/i.test(descriptor)) return;
        if (platform === 'macos' && /windows|linux|\.exe$/i.test(descriptor)) return;
        if (platform === 'linux' && /windows|macos|darwin|\.(?:exe|dmg|pkg)$/i.test(descriptor)) return;
        // Windows customers need one installer that works on both architectures.
        const architecture = metadata(ancestry, ['architecture', 'arch']).toLowerCase();
        const explicitArchitecture = /x64|arm64|amd64|x86|win32|ia32/i.test([...nextPath, pathname].join(' '));
        const singleArchitecture = /^(?:x64|arm64|amd64|x86|win32|ia32)$/.test(architecture);
        if (platform === 'windows' && (explicitArchitecture || singleArchitecture)) return;
        const rank = (universal ? 100 : /setup|installer/.test(descriptor) ? 60 : 20)
          + (nextPath.includes('latest') ? 10 : 0);
        candidates.push({
          url,
          rank,
          version: metadata(ancestry, ['version', 'latestversion', 'versionname', 'tagname']),
          size: metadata(ancestry, ['sizebytes', 'filesizebytes', 'bytes', 'size', 'filesize', 'contentlength']),
          date: metadata(ancestry, ['releasedate', 'releasedat', 'publishedat', 'date', 'published']),
          sha256: metadata(ancestry, ['sha256', 'sha256sum', 'sha256hash'])
        });
      });
    };
    walk(manifest);
    candidates.sort((a, b) => {
      const versionOrder = compareVersion(a.version, b.version);
      const aDate = Date.parse(a.date);
      const bDate = Date.parse(b.date);
      const dateOrder = Number.isFinite(aDate) && Number.isFinite(bDate) ? bDate - aDate : 0;
      return versionOrder || dateOrder || b.rank - a.rank;
    });
    return candidates[0] || null;
  };

  class CgsDownloads extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.products = Array.from(this.querySelectorAll('[data-download-product]'));
      if (!this.products.length) return;
      this.initialized = true;
      this.onChange = (event) => {
        if (event.target.matches('[data-download-product]') && event.target.checked) this.load(event.target);
      };
      this.onRetry = () => this.load(this.products.find((product) => product.checked));
      this.addEventListener('change', this.onChange);
      this.querySelector('[data-download-retry]').addEventListener('click', this.onRetry);
      this.load(this.products.find((product) => product.checked) || this.products[0]);
    }

    disconnectedCallback() {
      this.controller?.abort();
      clearTimeout(this.timeout);
      this.removeEventListener('change', this.onChange);
      this.querySelector('[data-download-retry]')?.removeEventListener('click', this.onRetry);
      this.initialized = false;
    }

    element(name) { return this.querySelector(`[data-download-${name}]`); }
    text(name, value) { this.element(name).textContent = value || this.dataset.unavailableValue; }

    formatSize(value) {
      if (!value) return '';
      if (!/^\d+(?:\.\d+)?$/.test(value)) return value;
      const bytes = Number(value);
      if (!Number.isFinite(bytes) || bytes <= 0) return '';
      // Consumer-facing MB/KB labels (1024-based). Prefer MB once size is ~100 KiB+.
      let amount;
      let unit;
      let fractionDigits;
      if (bytes < 1024) {
        amount = bytes;
        unit = 'B';
        fractionDigits = 0;
      } else if (bytes < 100 * 1024) {
        amount = bytes / 1024;
        unit = 'KB';
        fractionDigits = 1;
      } else if (bytes < 1024 ** 3) {
        amount = bytes / (1024 ** 2);
        unit = 'MB';
        fractionDigits = amount < 10 ? 2 : 1;
      } else {
        amount = bytes / (1024 ** 3);
        unit = 'GB';
        fractionDigits = 2;
      }
      try {
        return `${new Intl.NumberFormat(this.dataset.locale, {
          minimumFractionDigits: unit === 'MB' && amount < 10 ? 1 : 0,
          maximumFractionDigits: fractionDigits
        }).format(amount)} ${unit}`;
      } catch (error) {
        return `${amount.toFixed(fractionDigits)} ${unit}`;
      }
    }

    formatDate(value) {
      if (!value) return '';
      // Preserve unfamiliar human-readable dates instead of guessing day/month order.
      if (!/^\d{4}-\d{2}-\d{2}(?:T.*)?$/.test(value)) return value;
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '';
      try {
        return new Intl.DateTimeFormat(this.dataset.locale, { dateStyle: 'medium', timeZone: 'UTC' }).format(date);
      } catch (error) {
        return value.slice(0, 10);
      }
    }

    async load(product) {
      if (!product) return;
      this.controller?.abort();
      clearTimeout(this.timeout);
      const controller = new AbortController();
      this.controller = controller;
      this.text('name', product.dataset.name);
      this.text('title', product.dataset.driverTitle);
      this.text('platform', product.dataset.platformLabel);
      ['version', 'size', 'date'].forEach((name) => this.text(name, ''));
      this.element('requirements').hidden = product.dataset.platform !== 'windows';
      this.element('setup').hidden = product.dataset.platform !== 'windows';
      this.element('checksum').hidden = true;
      this.element('checksum').open = false;
      this.text('sha256', '');
      const link = this.element('link');
      link.hidden = true;
      link.removeAttribute('href');
      this.element('retry').hidden = true;
      this.element('unavailable-body').hidden = true;
      this.element('details').setAttribute('aria-busy', 'true');
      this.text('status', this.dataset.loading);
      this.timeout = setTimeout(() => controller.abort(), 12000);

      try {
        const manifestUrl = secureUrl(product.dataset.manifestUrl);
        if (!manifestUrl) throw new Error('Missing manifest URL');
        const response = await fetch(manifestUrl, { signal: controller.signal, credentials: 'omit', cache: 'no-store' });
        if (!response.ok) throw new Error('Manifest unavailable');
        const manifest = await response.json();
        const download = normalizeManifest(manifest, response.url || manifestUrl, product.dataset.platform);
        if (!download) throw new Error('No compatible installer');
        if (controller !== this.controller || controller.signal.aborted || !this.isConnected) return;
        this.text('version', download.version);
        this.text('size', this.formatSize(download.size));
        this.text('date', this.formatDate(download.date));
        if (/^[a-f0-9]{64}$/i.test(download.sha256)) {
          this.text('sha256', download.sha256);
          this.element('checksum').hidden = false;
        }
        link.href = download.url;
        link.hidden = false;
        this.text('status', this.dataset.ready.replace('__PRODUCT__', product.dataset.name));
      } catch (error) {
        if (controller !== this.controller || !this.isConnected) return;
        this.text('status', this.dataset.unavailable);
        this.element('unavailable-body').hidden = false;
        this.element('retry').hidden = false;
      } finally {
        if (controller === this.controller) {
          clearTimeout(this.timeout);
          this.element('details').setAttribute('aria-busy', 'false');
        }
      }
    }
  }

  customElements.define('cgs-downloads', CgsDownloads);
})();
